import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {required} from '../../../../../../../shared/utils';
import {EditPermissions, NodeBuilder,} from '../../../../../../../shared/models';
import {getEditTypes} from '../../../delegatorUtils';
import isEmpty from 'lodash/isEmpty';
import {newStepValue} from "../../../../../services/flowBuilder/utils/constants";
import {ActiveFormService} from '../../../../../services/active-form.service';
import {DisplayTypeModalData} from "../../templates/display-type-dialog/displayTypeUtils";

const NODE_TYPE = 'dynamicList';

@Component({
    selector: 'app-dynamic-list',
    templateUrl: './dynamic-list.component.html',
    styleUrls: ['./dynamic-list.component.scss']
})
export class DynamicListComponent extends NodeBuilder implements OnInit, OnDestroy {

    menuParams: FormGroup = undefined;
    optionSeperatorList: string[] = ['. ', '- '];
    @Input() canEditFlows: boolean;

    canEdit: EditPermissions;
    validatiorFields = ['excludePattern', 'excludeValue', 'filterByPattern', 'filterByValue'];
    clearValidatiorsFields = ['excludeValue', 'excludePattern', 'filterByValue', 'filterByPattern'];
    metaDataObj: any = {};
    stepUniqueID: string = '';
    displayTypeModalData: DisplayTypeModalData = undefined;

    constructor(
        private activeFormService: ActiveFormService,
    ) {
        super(NODE_TYPE);
    }

    ngOnInit(): void {

    }

    initForm() {
        const {editMenuTextOnly, editFlows} = getEditTypes(this.canEdit);
        this.menuParams = new FormGroup({
            menuHeader: new FormControl({
                value: '',
                disabled: !editMenuTextOnly
            }),
            groupTitle: new FormControl({
                value: '',
                disabled: !editMenuTextOnly
            }),
            bodyText: new FormControl({
                value: '',
                disabled: !editMenuTextOnly
            }, [required('Message To User')]),
            varName: new FormControl({
                value: '',
                disabled: !editFlows
            }, [required('Variable Name')]),
            optionPattern: new FormControl({
                value: '',
                disabled: !editFlows
            }, [required('List Option Pattern')]),
            valuePattern: new FormControl({
                value: '',
                disabled: !editFlows
            }),
            descriptionPattern: new FormControl({
                value: '',
                disabled: !editFlows
            }),
            optionSeperator: new FormControl({
                value: '. ',
                disabled: !editFlows
            }),
            pagination: new FormControl({
                value: true,
                disabled: !editFlows
            }),
            nextOptionTitle: new FormControl({
                value: 'Next',
                disabled: !editFlows
            }),
            previousOptionTitle: new FormControl({
                value: 'Back',
                disabled: !editFlows
            }),
            filterByPattern: new FormControl({
                value: '',
                disabled: !editFlows
            }),
            filterByValue: new FormControl({
                value: '',
                disabled: !editFlows
            }),
            excludePattern: new FormControl({
                value: '',
                disabled: !editFlows
            }),
            excludeValue: new FormControl({
                value: '',
                disabled: !editFlows
            }),
            success: new FormControl({
                value: newStepValue,
                disabled: !editFlows
            },),
            error: new FormControl({
                value: newStepValue,
                disabled: !editFlows
            },),
            default: new FormControl({
                value: newStepValue,
                disabled: !editFlows
            },),
            displayType: new FormGroup({}),
            customListButton: new FormControl("")
        });
        this.activeFormService.setActiveForm(this.menuParams);
    }

    addSeparator(e, valueInput): void {
        e.stopPropagation();
        if (valueInput !== '') {
            this.optionSeperatorList.push(valueInput);
        }
    }

    get getStepData(): {} {
        const formData = this.menuParams.getRawValue();
        this.metaDataObj.nodeVersion = '1.2.0';
        if (this.stepUniqueID !== '') {
            this.metaDataObj = {
                ...this.metaDataObj,
                stepUniqueID: this.stepUniqueID
            }
        }

        let nodeData = {
            menu: this.getFormData(formData),
            type: 'menu',
            metadata: this.metaDataObj
        };
        return nodeData;
    }

    get isNodeValid(): boolean {
        this.validatiorFields.forEach((fieldName, index) => {
            if (this.menuParams.get(fieldName).value === '') {
                const clearValidationField = this.clearValidatiorsFields[index];
                this.menuParams.controls[clearValidationField].clearValidators();
                this.menuParams.get(clearValidationField).setErrors(null);
            }
        });
        this.menuParams.markAllAsTouched();
        return this.menuParams.valid;
    }

    set buildForm(stepData: any) {
        this.initForm();

        if (!isEmpty(stepData)) {
            const formData = this.stepToForm(stepData)
            this.stepUniqueID = stepData.metadata?.stepUniqueID ? stepData.metadata?.stepUniqueID : '';
            this.menuParams.patchValue(formData);
            if (!this.optionSeperatorList.includes(stepData.menu.menuParams.optionSeperator)) {
                this.optionSeperatorList.push(stepData.menu.menuParams.optionSeperator);
            }
        }
        this.setDisplayType(stepData?.menu?.menuParams?.displayTypes, stepData?.menu?.menuParams?.customListButton);
        this.patchInitialResultsStepNumber();
    }

    private patchInitialResultsStepNumber() {
        if (this.menuParams.get('success').value === newStepValue) {
            this.menuParams.get('success').setValue('step' + this.getNextStep());
        }
        if (this.menuParams.get('error').value === newStepValue) {
            this.menuParams.get('error').setValue('step' + this.getNextStep());
        }
        if (this.menuParams.get('default').value === newStepValue) {
            this.menuParams.get('default').setValue('OPTIONAL');
        }
    }

    stepToForm(stepData) {
        const menu = stepData.menu;
        const stepMetaData = stepData.metadata;
        const filterBy = this.getPatternValue(menu.menuParams.filterBy);
        const exclude = this.getPatternValue(menu.menuParams.exclude);
        return {
            menuHeader: menu.menuHeader,
            groupTitle: menu.menuParams.groupTitle,
            bodyText: menu.bodyText,
            varName: menu.input.varName,
            optionPattern: menu.menuParams.optionPattern,
            valuePattern: menu.menuParams.valuePattern,
            descriptionPattern: menu.menuParams.descriptionPattern,
            optionSeperator: menu.menuParams.optionSeperator,
            pagination: menu.menuParams.pagination,
            nextOptionTitle: menu.menuParams.nextOptionTitle,
            previousOptionTitle: menu.menuParams.previousOptionTitle,
            displayTypes: menu.menuParams.displayTypes,
            filterByPattern: filterBy.pattern,
            filterByValue: filterBy.value,
            excludePattern: exclude.pattern,
            excludeValue: exclude.value,
            success: menu.results.success.next,
            error: menu.results.error.next,
            default: menu.results.default?.next ? menu.results.default.next : 'OPTIONAL'
        }
    }

    getPatternValue(objData) {
        const data = Object.keys(objData);
        return {
            pattern: data[0] ? data[0] : '',
            value: data[0] ? objData[data[0]] : ''
        }
    }

    getFormData(formData) {
        return {
            menuType: NODE_TYPE,
            menuHeader: formData.menuHeader,
            bodyText: formData.bodyText,
            results: {
                success: {
                    next: formData.success,
                },
                error: {
                    next: formData.error,
                },
                default: {
                    next: formData.default ? formData.default : 'OPTIONAL',
                },
            },
            menuParams: {
                groupTitle: formData.groupTitle,
                optionPattern: formData.optionPattern,
                valuePattern: formData.valuePattern,
                descriptionPattern: formData.descriptionPattern,
                optionSeperator: formData.optionSeperator,
                pagination: formData.pagination,
                nextOptionTitle: formData.nextOptionTitle,
                previousOptionTitle: formData.previousOptionTitle,
                displayTypes: formData.displayType,
                customListButton: formData.customListButton,
                filterBy: formData.filterByPattern && formData.filterByValue ? {
                    [formData.filterByPattern]: formData.filterByValue
                } : {},
                exclude: formData.excludePattern && formData.excludeValue ? {
                    [formData.excludePattern]: formData.excludeValue
                } : {}
            },
            input: {
                isPrivateVariable: false,
                varName: formData.varName
            },
        }
    }

    getformValue(form: FormGroup, controlName: string) {
        return form.get(controlName).value;
    }

    ngOnDestroy(): void {
        this.activeFormService.clearActiveForm();
    }


    /**
     *
     * @param nodeDisplayType The current displayType config -> 'number' || {8, 'NUMBER', 13: 'LIST'}
     * @param nodeCustomListButton The current customListButton -> 'string' || Options
     * @private Gets all the channels that are configured for this client and sets the new displayType structure
     */
    private setDisplayType(nodeDisplayType: {}, nodeCustomListButton: string) {
        const displayType = nodeDisplayType ? nodeDisplayType : {};
        const customListButton = nodeCustomListButton ? nodeCustomListButton : "";
        this.displayTypeModalData = {
            isDirty: false,
            displayType,
            customListButton
        }
        if (!isEmpty(displayType)) {
            this.patchDisplayType(this.displayTypeModalData);
        }
    }

    displayChange(event) {
        this.patchDisplayType(event);
    }

    private patchDisplayType(event) {
        const displayTypeFormGroup: FormGroup = this.menuParams.get('displayType') as FormGroup
        for (const channelId in event?.displayType) {
            const displayTypeValue = event?.displayType[channelId]
            if (displayTypeFormGroup.get(channelId)) {
                displayTypeFormGroup.get(channelId).setValue(displayTypeValue)
            } else {
                displayTypeFormGroup.addControl(channelId, new FormControl(displayTypeValue));
            }
        }
        if(event.isDirty) {
            displayTypeFormGroup.markAsDirty();
        }
        this.menuParams.get('customListButton').setValue(event?.customListButton);
    }

}
